import { useEffect, useMemo, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import BotProjetIntegrationPlanTab from './BotProjetIntegrationPlanTab';
import BotAccessDiagramModal from './BotAccessDiagramModal';
import {
  Shield, Zap, HardDrive, Activity, Timer, Settings, X, Database, BrainCircuit, KeyRound, Wifi, MapPin, ScrollText, Skull,
  TrendingUp, TrendingDown, Wallet, Clock, ListChecks, Gauge, Workflow
} from 'lucide-react';

// Types
interface Bot {
  id?: string;
  botSlug?: string;
  name: string;
  avatar?: string;
  deceased?: boolean;
  owner: string;
  spirit: string;
  mandate: string;
  pilots: string;
  allowed: string[];
  forbidden: string[];
  metadata?: Record<string, unknown>;
}

interface BotDetailModalProps {
  bot: Bot | null;
  bots?: Bot[];
  isOpen: boolean;
  onClose: () => void;
  onSelectBot?: (bot: Bot) => void;
}

type TabId = 'esprit' | 'securite' | 'acces' | 'skills' | 'ai' | 'cron' | 'telegram' | 'integrations' | 'roi';

type OpenClawAgentEntry = {
  id: number | string;
  tabId: string;
  category?: string | null;
  title?: string | null;
  body?: string | null;
  sortOrder?: number | null;
};

type DbBot = {
  id: string;
  botSlug?: string;
  name: string;
  runtime?: string;
  agentId?: string;
  primaryChannel?: string;
  description?: string;
  metadata?: Record<string, unknown>;
};

type BotAccessUserSnapshot = {
  id: string;
  name: string;
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
  telegramId?: string | null;
  accountKind?: string | null;
  isPrimary?: boolean;
  relationType?: string | null;
};

type AccessSnapshot = {
  access_users?: BotAccessUserSnapshot[];
  github_access?: GitHubAccessMeta | null;
  database_access?: DatabaseAccessMeta | null;
};

type RoiData = {
  botName?: string;
  currency: string;
  period: { label: string; since: string | null; until: string | null; monthsCovered: number };
  config: {
    configured: boolean;
    hourlyRate: number | null;
    effectiveHourlyRate: number | null;
    rateIsDefault: boolean;
    minutesSavedPerRequest: number | null;
    valuePerRequest: number | null;
    costPerRequest: number | null;
  };
  totals: {
    requests: number;
    requestsByStatus: { status: string; count: number }[];
    activities: number;
    activitiesByType: { type: string; count: number; minutesSaved: number | null }[];
    minutesSaved: number;
    humanMinutesWithout: number;
    humanMinutesWith: number;
  };
  value: { amount: number | null; basis: string };
  costs: {
    fixedMonthly: number | null;
    fixedOneTime: number | null;
    fixedPeriodTotal: number | null;
    fixedItems: { label: string; amount: number; cadence: string; monthlyEquivalent: number }[];
    usage: number | null;
    usageBasis: string;
    total: number | null;
  };
  roi: { net: number | null; ratio: number | null; percent: number | null };
  timeline: { month: string; requests: number; activities: number }[];
  meta: { tablesUsed: string[]; notes: string[] };
};

const baseTabs: { id: TabId; label: string; icon: React.ElementType }[] = [
  { id: 'esprit', label: 'Esprit & Objectifs', icon: Shield },
  { id: 'securite', label: 'Sécurité', icon: HardDrive },
  { id: 'acces', label: 'Accès', icon: KeyRound },
  { id: 'skills', label: 'Skills', icon: Zap },
  { id: 'ai', label: 'Intelligence Artificielle', icon: BrainCircuit },
  { id: 'cron', label: 'Cron & Jobs', icon: Timer },
  { id: 'telegram', label: 'Telegram', icon: Settings },
  { id: 'integrations', label: 'Plan d’intégrations', icon: ScrollText },
  { id: 'roi', label: 'ROI & Coûts', icon: Activity },
];

// Données partagées communes entre bots
const commonAI = {
  provider: 'OpenRouter',
  cascade: [
    { level: 'Défaut', model: 'DeepSeek V3.2', usage: 'Tâches de routine, faible coût, haute vitesse.' },
    { level: 'Intermédiaire', model: 'Gemini 2.5 Pro', usage: 'Analyse multi-documents, complexité moyenne, raisonnement.' },
    { level: 'Élevé', model: 'GPT-5.4', usage: 'Haute précision, décisions critiques, architecture complexe, ambiguïté.' },
  ],
  logic: "Bascule automatique vers un modèle supérieur si la complexité l'exige, et retour au modèle par défaut après ~5 échanges simples ou à la fin d'une tâche complexe.",
};

const commonAccess = {
  'Microsoft 365': 'Accès complet aux services M365 (Email, Calendrier, Tâches, OneDrive)',
  'Google Suite': 'Accès aux services Google (Gmail, Calendar, Tasks, Drive)',
  'Système de Fichiers': 'Accès complet en lecture/écriture au workspace OpenClaw sur le disque local.',
  'Réseau': 'Accès sortant pour les recherches web (Brave/DDG) et les appels API.',
  'ClawHub': 'Accès à l\'installation auto de skills via clawhub.com'
};

const commonSecurity = {
  location: '900 SE 10th Street, Pompano Beach, FL 33060',
  network: 'VLAN poste de travail, non-serveur.',
};

const commonSkills = {
  'AI & Modèles': ['model-cascade', 'model-router', 'openai-image-gen', 'ai-avatar-generation', 'image'],
  'Communication': ['microsoft-365-mail', 'google-mail', 'microsoft-365-calendar', 'google-calendar', 'microsoft-365-tasks', 'google-tasks'],
  'Documentation': ['markdown-converter', 'pdf-generator', 'word-docx', 'word-to-pdf'],
  'Gestion de Fichiers': ['read', 'write', 'edit', 'microsoft-365-onedrive'],
  'Recherche': ['web_search', 'ddg-web-search'],
  'Exécution': ['exec', 'process', 'cron-manager'],
  'Sécurité & Méta': ['clawhub', 'skill-manager', 'healthcheck', 'skill-creator']
};

const botSpecificData: Record<string, any> = {
  'Cyberta': {
    security: {
      ...commonSecurity,
      risks: [
        { title: 'Accès ERP en lecture', description: 'Cyberta consulte certaines tables ERP ciblées. Le risque principal est l’exposition ou la mauvaise interprétation d’informations commerciales si le périmètre n’est pas maintenu au moindre privilège.', level: 'Modéré' },
        { title: 'Tunnel des ventes', description: 'Les informations du tunnel des ventes peuvent influencer les suivis commerciaux. Cyberta doit préparer et synthétiser, sans décider ni modifier le pipeline de façon autonome.', level: 'Modéré' },
      ],
    },
    access: {
      'Base de données ERP': 'Accès en lecture seulement à certaines tables ciblées, limitées au soutien des processus de vente autorisés.',
      'Tunnel des ventes': 'Consultation, synthèse et préparation de suivis commerciaux. Aucune modification critique sans validation humaine.',
      'Gouvernance': 'Deny by default, moindre privilège, traçabilité des accès et approbation requise pour toute extension de périmètre.',
    },
    skills: {
      'Ventes': ['synthèse commerciale', 'suivi opportunités', 'préparation de rencontres', 'analyse tunnel des ventes'],
      'Données': ['lecture ERP encadrée', 'rapprochement commercial', 'résumés opérationnels'],
    },
    ai: commonAI,
    telegram: {
      policy: 'Utilisation encadrée par l’équipe des ventes et le propriétaire Eric Durand.',
      streaming: 'Partiel',
      special_powers: 'Peut préparer des analyses et suivis commerciaux; ne peut pas engager BZ ni modifier les données critiques sans validation.',
    },
  },
  'Lola': {
    security: {
      ...commonSecurity,
      risks: [
        { title: 'Accès Git marketing', description: 'Lola peut préparer des changements sur plusieurs dépôts web. Le risque principal est la publication non validée ou l’altération de l’image publique de BZ.', level: 'Élevé' },
        { title: 'Bases de données web / Orbit', description: 'Les bases associées aux sites, contact.bzinc.ca et Orbit peuvent contenir des données sensibles ou opérationnelles. Toute modification doit être justifiée, traçable et approuvée.', level: 'Élevé' },
      ],
    },
    access: {
      'Git - site web principal': 'Accès au dépôt du site web pour analyse, préparation de branches et propositions de changements.',
      'Git - contact.bzinc.ca': 'Accès au dépôt de la page contact.bzinc.ca pour analyse et préparation de changements encadrés.',
      'Git - Orbit': 'Accès au dépôt Orbit pour soutien marketing et technique selon mandat.',
      'Bases de données web': 'Accès aux bases du site web principal, de contact.bzinc.ca et de Orbit selon besoin marketing approuvé.',
      'Gouvernance': 'Modifications traçables, revue humaine avant production, séparation entre préparation et publication.',
    },
    skills: {
      'Marketing': ['analyse contenu', 'optimisation web', 'préparation campagne', 'révision textes'],
      'Technique': ['Git', 'revue de branches', 'analyse bases web', 'documentation changements'],
    },
    ai: commonAI,
    telegram: {
      policy: 'Utilisation pilotée par Rosalie Émond, avec Eric Durand comme propriétaire.',
      streaming: 'Partiel',
      special_powers: 'Peut préparer des contenus, recommandations et changements techniques; ne publie pas en production sans validation.',
    },
  },
  'Danyboubot': {
    security: {
      ...commonSecurity,
      risks: [
        { title: "Skill d'auto-installation (clawhub)", description: "Le bot peut installer de nouveaux skills sur lui-même via Telegram, un vecteur de risque si une commande est mal interprétée ou malveillante.", level: 'Élevé' },
        { title: 'Accès M365 (Email / Calendrier)', description: "Lecture/écriture sur la boîte mail danyboubot@bouinvest.com et le calendrier associé. Risque de fuite d'information ou d'envoi non sollicité.", level: 'Modéré' }
      ]
    },
    access: {
      ...commonAccess,
      'PRIsME-code': 'Lecture/écriture sur le repo de code PRIsME-Master-BZ.',
      'Base de Données Railway': 'Accès en lecture à la base de données des projets via le skill `dany-project-db`.',
    },
    skills: {
      ...commonSkills,
      'Base de Données': ['dany-project-db'],
    },
    ai: commonAI,
    cron: {
      jobs: [
        'Daily Iran War Summary (6:00 AM America/New_York)',
        'Weekly Project List (Monday 7:00 AM America/New_York)',
      ]
    },
    telegram: {
      policy: 'Accès DM direct et sur liste blanche pour les groupes.',
      streaming: 'Partiel',
      special_powers: 'Peut installer des skills via `clawhub`, redémarrer le service, et gérer les autres sessions.'
    }
  },
  'BotTelecom': {
    security: {
      ...commonSecurity,
      risks: [
        { title: "Skill d'auto-installation (clawhub)", description: "Auto-installation de skills via Telegram - risque de code non vérifié ou malveillant.", level: 'Élevé' },
        { title: 'Accès M365 & Gmail', description: "Accès complet aux services Microsoft 365 et Gmail pour courriel, calendrier, tâches et fichiers.", level: 'Modéré' }
      ]
    },
    access: {
      ...commonAccess,
      'PRIsME-code': 'Accès en lecture/écriture au code PRIsME pour les besoins Telecom.',
    },
    skills: {
      ...commonSkills,
      // Pas de base de données pour le moment
    },
    ai: commonAI,
    cron: {
      jobs: [
        'Daily report - Liste des tâches du jour et risques identifiés (envoyé à Dany Bourassa)',
      ]
    },
    telegram: {
      policy: 'Accès DM direct et sur liste blanche pour les groupes.',
      streaming: 'Partiel',
      special_powers: 'Peut installer des skills via `clawhub`, redémarrer le service, et gérer les autres sessions.'
    }
  },
  'Oscar': {
    security: {
      location: 'À définir selon déploiement',
      network: 'VLAN approprié selon déploiement',
      risks: [
        { title: 'Accès administrateur étendu', description: "Accès Admin/Root et droits d'ajout pour plusieurs gestionnaires - nécessite une traçabilité stricte.", level: 'Élevé' },
        { title: 'Modification du code PRIsME', description: "Capacité de modifier le code source de PRIsME Master BZ - risque d'intégrité du code.", level: 'Élevé' }
      ]
    },
    access: {
      'PRIsME Master BZ Code': 'Accès complet en lecture/écriture au code source PRIsME Master BZ.',
      'Repos Git': 'Accès aux repositories PRIsME sur GitHub.',
      'Base de données PRISME Master': 'Accès en lecture et écriture à la base de données prisme_master sur 100% des tables.',
      'Fichiers de documentation': 'Création/modification des fichiers de documentation PRIsME.',
      'Gestionnaires avec droits': 'Accès selon hiérarchie : Admin/Root (3) + Gestionnaires avec droits (12)',
    },
    skills: {
      'Programmation & orchestration': ['exec', 'process', 'read', 'write', 'edit', 'clawflow', 'clawflow-inbox-triage', 'tmux'],
      'Git & livraison': ['github', 'gh-issues', 'clawhub'],
      'Documentation & contenu': ['document-pro', 'pdf-generator', 'logo-generator'],
      'Sécurité & plateforme': ['healthcheck', 'node-connect', 'skill-guard'],
      'Communication & productivité': ['himalaya', 'weather'],
      'SQL & base de données': ['pg'],
      'Création de skills': ['skill-creator'],
    },
    ai: commonAI,
    cron: {
      jobs: [
        'Synchronisation automatique des contributions PRIsME',
        'Vérification d’intégrité du code et documentation',
      ]
    },
    telegram: {
      policy: 'Accès contrôlé aux Admin/Root et gestionnaires autorisés.',
      streaming: 'Partiel',
      special_powers: 'Capacité de programmer et modifier PRIsME Master BZ directement.'
    }
  },
  'Flavius': {
    security: {
      location: 'Workspace OpenClaw BZ / environnement de travail contrôlé',
      network: 'VLAN poste de travail, deny by default, accès externes seulement par mandat explicite.',
      risks: [
        { title: 'Accès aux bases projets old/new', description: 'Flavius peut lire, comparer, valider et migrer la donnée projet. Toute écriture de masse ou clonage doit rester approuvée et vérifiée.', level: 'Élevé' },
        { title: 'Programmation directe dans PRIsME / TactiCE', description: 'Capacité de modifier la section /projets, le code OpenClaw et les skills locaux. Risque principal: altération fonctionnelle si changement non validé ou non buildé.', level: 'Élevé' },
        { title: 'Secrets workspace (.env / GitHub token)', description: 'Présence de secrets d’intégration dans l’environnement local. Exige discipline stricte: jamais exposer les secrets en réponse ou dans le frontend.', level: 'Modéré' }
      ]
    },
    access: {
      'PRIsME Master BZ': 'Clone local complet du dépôt, analyse, édition, build, commit et push GitHub sur demande.',
      'Section /projets (TactiCE intégré)': 'Accès de développement à la page pont `src/pages/Projets.tsx`, aux composants `projet/src/components/*` et au backend `/api/projet`.',
      'Base projets — ancienne': 'Connexion PostgreSQL active à `DATABASE_PROJETS_OLD` pour inventaire, validation, comparaison et clonage contrôlé.',
      'Base projets — nouvelle': 'Connexion PostgreSQL active à `DATABASE_PROJETS` pour validation, migration et travail applicatif sur la cible courante.',
      'OpenClaw Workspace': 'Lecture/écriture sur skills, mémoire, docs locales, scripts et artefacts de build.',
      'GitHub': 'Accès via `GITHUB_TOKEN` pour synchronisation du repo PRIsME et push des changements approuvés.',
      'Telegram': 'Canal direct de pilotage pour demandes exécutives, suivi projet et interventions opérationnelles.',
      'Microsoft 365': 'Prévu au cadre, mais non actif tant que les variables MS365 ne sont pas renseignées et validées.'
    },
    skills: {
      'Projet & Delivery': ['projets-db', 'prisme-dev', 'taskflow', 'taskflow-inbox-triage'],
      'Création & gouvernance de skills': ['skill-creator', 'clawhub'],
      'Sécurité & posture': ['healthcheck', 'node-connect'],
      'Intégrations & orchestration': ['mcporter', 'tmux'],
      'Outils cœur OpenClaw': ['read', 'write', 'edit', 'exec', 'process', 'web_fetch', 'image', 'memory_search', 'memory_get'],
      'Contexte & support': ['weather']
    },
    ai: {
      provider: 'openai-codex / GPT-5.4 (session principale OpenClaw)',
      cascade: [
        { level: 'Défaut', model: 'openai-codex/gpt-5.4', usage: 'Programmation, analyse PRIsME, gouvernance, diagnostics de base de données et synthèse exécutive.' },
        { level: 'Vision', model: 'Image model configuré par OpenClaw', usage: 'Lecture d’avatars, captures, schémas et validation visuelle lorsque requis.' },
        { level: 'Délégation', model: 'Sous-agent / ACP au besoin', usage: 'Travaux plus lourds ou parallélisables, avec validation et supervision par Flavius.' },
      ],
      logic: 'Flavius privilégie le mode direct et rigoureux: il agit d’abord avec le modèle principal, délègue seulement quand le volume ou la complexité le justifient, puis revient avec une synthèse prouvée (build, diff, logs, comptages ou inspection).',
    },
    cron: {
      jobs: [
        'Heartbeats OpenClaw pour relance proactive, maintenance mémoire et suivi des éléments utiles.',
        'Routines à la demande: validation de build, inventaire DB, comparaison old/new, packaging de skills, commit/push sur mandat.',
        'Aucune automatisation sensible permanente sans cadrage explicite dans HEARTBEAT.md ou futur cron dédié.',
      ]
    },
    telegram: {
      policy: 'DM direct en français, ton exécutif, professionnel, avec interventions utiles seulement. Réponses vers Telegram pilotées par OpenClaw, sans contournement shell.',
      streaming: 'Minimal / orienté résultat',
      special_powers: 'Peut coder dans PRIsME, bâtir et packager des skills, interroger les bases projets, effectuer des validations techniques et pousser sur GitHub lorsque demandé explicitement.'
    }
  },
  'Zaz': {
    security: {
      location: 'bz-hermes-zaz / workspace Hermes local contrôlé',
      network: 'Gateway Hermes actif sous systemd avec linger; Telegram configuré mais polling récemment dégradé; posture deny by default.',
      risks: [
        { title: 'Accès multi-systèmes', description: 'Zaz dispose d’accès testés à PRIsME, Asana, Google Workspace, GitHub, Telegram et PostgreSQL. Toute action externe doit rester demandée, limitée et traçable.', level: 'Élevé' },
        { title: 'Secrets et chaînes de connexion', description: 'Des credentials existent dans l’environnement Hermes. Seuls leurs noms et états de test peuvent être documentés; leurs valeurs et URLs sensibles sont interdites d’affichage.', level: 'Élevé' },
        { title: 'Intégrations partiellement disponibles', description: 'Le MCP google_workspace est parqué faute de uvx, le polling Telegram signale des erreurs récentes et GITHUB_TOKEN_ZAZ ne voit pas le dépôt. Il faut conserver ces distinctions plutôt que déclarer un accès globalement sain.', level: 'Modéré' },
      ]
    },
    access: {
      '[TESTÉ] PRIsME Master BZ — GitHub': 'Remote lisible et API du dépôt privé BZinc-DB/PRIsME_Master_BZ accessible avec GITHUB_TOKEN; push=true, admin=false, maintain=false. GITHUB_TOKEN_ZAZ retourne 404 pour ce dépôt. Le CLI gh n’est pas installé.',
      '[TESTÉ] PostgreSQL': 'DATABASE_PRISME_MASTER : 76 tables publiques; PRIsME_agent_info : 22; DATABASE_PRISME_FILES : 2; DATABASE_PRISME_PROJET : 56; DATABASE_PRISME_PROJETNEW : 116. Tests de lecture seulement; aucune table recréée.',
      '[TESTÉ] PRIsME_agent_info': 'Connexion active et lecture confirmée. La synchronisation autorisée demeure limitée aux opérations update/insert prévues; recréer, supprimer ou migrer les tables est interdit.',
      '[TESTÉ] Asana': 'ASANA_TOKEN configuré; appel API users/me réussi (HTTP 200).',
      '[TESTÉ] Google Workspace direct': 'OAuth Hermes authentifié; lectures minimales Gmail, Calendar et Drive réussies sans envoi ni modification. Compte assistant par défaut : zaz@bzinc.ai.',
      '[INDISPONIBLE] MCP Google Workspace': 'Configuration présente, mais serveur parqué parce que uvx est absent. Cette panne ne remet pas en cause les outils Google Workspace directs testés séparément.',
      '[TESTÉ, ÉTAT DÉGRADÉ] Telegram': 'Credential bot validé par getMe. Le gateway Hermes est actif, mais les journaux récents signalent des erreurs et reprises de polling; la santé de livraison n’est pas déclarée complète.',
      '[ACTIF] Hermes local': 'Hermes Agent 0.21.1 (2026.9.7), host Linux bz-hermes-zaz, profil default, workspace /root/openclaw-workspace, gateway systemd actif avec linger, mémoire intégrée active, aucune alerte de sécurité Hermes active. Mise à jour disponible.',
      '[DISPONIBLE] Système & outils': 'Lecture/écriture de fichiers, terminal, psql, Python, Node/npm, web, Browser Use, Chromium Playwright, vision, PDF, TTS, génération d’images, délégation, documents et cron, selon mandat.',
      '[INDISPONIBLE OU NON VALIDÉ] Autres canaux': 'Discord sans token; Slack, WhatsApp, Teams et Signal non validés comme connectés. Les toolsets browser et browser-cdp ont des dépendances manquantes, malgré Browser Use disponible.',
    },
    skills: {
      'Hermes cœur': ['file', 'terminal', 'code_execution', 'skills', 'memory', 'session_search', 'todo', 'delegation', 'cronjob'],
      'Recherche & médias': ['web search', 'web extract', 'Browser Use', 'vision', 'image_gen', 'pdf', 'tts'],
      'Google Workspace testé': ['gmail search/read', 'drive search/read/download', 'calendar list/manage — écriture sur autorisation'],
      'GitHub & code': ['skill github disponible', 'git local', 'API GitHub', 'npm/node', 'Python', 'psql'],
      'Intégrations métier testées': ['Asana API', 'PostgreSQL PRIsME', 'Telegram Bot API'],
      'Hermes orchestration': ['gateway', 'cron', 'delegate_task', 'kanban runtime-gated', 'mémoire intégrée'],
    },
    ai: {
      provider: 'openai-codex / gpt-5.6-sol (session Hermes Zaz)',
      cascade: [
        { level: 'Principal testé', model: 'openai-codex/gpt-5.6-sol', usage: 'Analyse PRIsME, code, rapports exécutifs, orchestration et décisions outillées dans la session courante.' },
        { level: 'Authentification', model: 'OpenAI Codex OAuth', usage: 'Provider connecté selon Hermes Doctor; OpenRouter, Nous Portal, MiniMax OAuth et xAI OAuth ne sont pas connectés.' },
        { level: 'Image configurée non testée', model: 'openai-codex/gpt-image-2-high', usage: 'Génération d’images configurée, sans test média pendant cet audit.' },
        { level: 'STT configuré non testé', model: 'Local, langue anglaise', usage: 'Transcription locale activée; aucune entrée audio testée pendant cet audit.' },
      ],
      logic: 'Zaz distingue explicitement configuré, disponible, testé, dégradé et interdit. Une conclusion exige une preuve actuelle : appel API minimal, lecture DB, statut Hermes, diff, build ou vérification Git.',
    },
    cron: {
      jobs: [
        '8 jobs Hermes actifs; 0 job inactif dans hermes cron list --all. Livraison configurée : local.',
        'Suivi quotidien Asana Eric – projets en retard — jours ouvrables 08:00 America/Toronto; dernier run OK.',
        'Sync PRIsME agent info Hermes — quotidien 00:00 America/Toronto; dernier run OK.',
        'Auto-analyse gouvernance Zaz Hermes — samedi 22:00 America/Toronto; exécution en cours pendant l’audit.',
        'Rapports PRIsME tactique, stratégique et opérationnel — mardi 08:00; actifs, aucune exécution encore enregistrée.',
        'Mise à jour hebdomadaire LEX PRIsME — dimanche 04:00; active, aucune exécution encore enregistrée.',
        'Création mensuelle préparation Actualité PRIsME – vendredi — vendredi 08:00; job no-agent actif, aucune exécution encore enregistrée.',
      ]
    },
    telegram: {
      policy: 'Canal Hermes configuré. Credential bot testé par getMe; propriétaires non confirmés à noter “propriétaire non confirmé”. Toute action sortante reste encadrée par le mandat.',
      streaming: 'Streaming global Hermes désactivé; réponse orientée résultat.',
      special_powers: 'Peut rechercher dans PRIsME, modifier le repo sur mandat, produire des rapports, exécuter la synchronisation PRIsME_agent_info sans recréer les tables et auditer les accès. Les erreurs récentes de polling doivent être résolues avant de qualifier le canal de pleinement sain.'
    }
  },
  Oracle: {
    access: {
      'Workspace & commandes serveur': 'Lire/modifier des fichiers dans le workspace et exécuter des commandes serveur.',
      OpenClaw: 'Gérer/inspecter OpenClaw : statut, sessions, agents, mémoire, sous-agents.',
      Telegram: 'Répondre ici sur Telegram.',
      'Navigateur automatisé': 'Navigateur contrôlable pour automatiser des pages web.',
      'Recherche web': 'Recherche web et lecture de pages web.',
      'PDF & image': 'Analyse PDF et image.',
      'Génération d’images': 'Génération d’images.',
      'TTS / voix': 'TTS/voix si demandé.',
      'Google Workspace': 'Gmail, Drive et Calendar (outils branchés côté serveur).',
      'Git / GitHub': 'Utilisable via outils locaux/skills, selon les identifiants déjà configurés.',
      'Politiques OpenClaw': 'Fichiers/nœuds OpenClaw seulement si les politiques de lecture/écriture l’autorisent.',
      'GitHub — PRIsME_Master_BZ': 'https://github.com/danybourassa1977/PRIsME_Master_BZ.git',
      'GitHub — PRIsME_Intendant': 'https://github.com/danybourassa1977/PRIsME_Intendant.git',
      'GitHub — PRIsME_NyxHemera': 'https://github.com/danybourassa1977/PRIsME_NyxHemera.git',
      'GitHub — PRIsME_ChateauLaurier': 'https://github.com/danybourassa1977/PRIsME_ChateauLaurier.git',
      'GitHub — PRIsME_VentilationCF': 'https://github.com/danybourassa1977/PRIsME_VentilationCF.git',
    },
    skills: {
      'Skills locaux/personnalisés': [
        'github-dev-helper — aide GitHub/dev/repos',
        'gmail — aide Gmail',
        'google-calendar — calendrier Google',
        'notebooklm-skill — NotebookLM',
        'notebooklm-ppt — génération PPT via NotebookLM',
      ],
      'Skills OpenClaw disponibles': [
        '1password', 'apple-notes', 'apple-reminders', 'bear-notes',
        'blogwatcher', 'blucli', 'camsnap', 'canvas', 'clawhub',
        'coding-agent', 'discord', 'eightctl', 'gemini',
        'gh-issues', 'gifgrep', 'github', 'gog', 'goplaces',
        'healthcheck', 'himalaya', 'imsg', 'mcporter',
        'model-usage', 'nano-pdf', 'node-connect', 'notion',
        'obsidian', 'openai-whisper-api', 'openai-whisper',
        'openhue', 'oracle', 'ordercli', 'peekaboo',
        'sag', 'session-logs', 'sherpa-onnx-tts',
        'skill-creator', 'slack', 'songsee', 'sonoscli',
        'spotify-player', 'summarize', 'taskflow',
        'taskflow-inbox-triage', 'things-mac', 'tmux',
        'trello', 'video-frames', 'voice-call', 'wacli',
        'weather', 'xurl',
      ],
    },
  },
};

const getBotData = (botName: string) => {
  return botSpecificData[botName] || {};
};

// --- Composants UI ---
const DataPlaceholder = ({ title }: { title: string }) => (
  <div className="flex flex-col items-center justify-center h-full text-center p-8 rounded-2xl bg-slate-800/50 border border-dashed border-slate-700">
    <Database className="w-12 h-12 text-slate-500 mb-4" />
    <h3 className="text-lg font-bold text-white">{title}</h3>
    <p className="text-sm text-slate-400 mt-1">Cette section nécessite une base de données pour l'historique et les métriques.</p>
  </div>
);

const Card = ({ children, className = '' }: { children: React.ReactNode, className?: string }) => (
  <div className={`rounded-xl border border-white/10 bg-slate-800/50 p-4 ${className}`}>{children}</div>
);

const CardTitle = ({ children }: { children: React.ReactNode }) => (
  <h4 className="font-bold text-white mb-2">{children}</h4>
);

const normalizeEntryTabId = (value: string | null | undefined) => {
  const key = String(value || '').toLowerCase().replace(/[^a-z0-9]/g, '');
  if (['skills', 'skill', 'competences', 'competence'].includes(key)) return 'skills';
  if (['ai', 'ia', 'intelligenceartificielle', 'intelligenceartificial', 'artificialintelligence'].includes(key)) return 'ai';
  if (['cron', 'cronjob', 'cronjobs', 'jobs', 'job', 'scheduledjobs', 'tachesrecurrentes'].includes(key)) return 'cron';
  if (['telegram', 'telegramconfig', 'telegrampolicy'].includes(key)) return 'telegram';
  return String(value || '').toLowerCase();
};

const entryTabId = (entry: OpenClawAgentEntry) => normalizeEntryTabId(entry.tabId);

type GitHubAccessMeta = {
  has_access?: boolean;
  summary?: string;
  token_verified?: boolean;
  repos?: Array<{
    url?: string;
    host?: string;
    repo?: string;
    repo_path?: string;
    remote_name?: string;
    access_level?: string;
    verified?: boolean;
    private?: boolean;
    source_kind?: string;
  }>;
};

type DatabaseAccessMeta = {
  has_access?: boolean;
  summary?: string;
  source?: string;
  matched_role?: string | null;
  role_candidates?: string[];
  databases?: Array<{
    database?: string;
    owner?: string;
    can_connect?: boolean;
    is_owner?: boolean;
    is_prisme_agent_info?: boolean;
  }>;
};

const extractGithubAccess = (metadata?: Record<string, unknown>): GitHubAccessMeta | null => {
  const value = metadata?.github_access;
  return value && typeof value === 'object' ? (value as GitHubAccessMeta) : null;
};

const extractDatabaseAccess = (metadata?: Record<string, unknown>): DatabaseAccessMeta | null => {
  const value = metadata?.database_access;
  return value && typeof value === 'object' ? (value as DatabaseAccessMeta) : null;
};

const DynamicEntries = ({ entries, emptyTitle }: { entries: OpenClawAgentEntry[]; emptyTitle: string }) => {
  if (!entries.length) return <DataPlaceholder title={emptyTitle} />;

  return (
    <div className="space-y-4">
      {entries.map((entry) => (
        <Card key={entry.id}>
          {entry.category ? (
            <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-indigo-200">{entry.category}</p>
          ) : null}
          <CardTitle>{entry.title || 'Information'}</CardTitle>
          {entry.body ? (
            <p className="text-sm text-slate-300 whitespace-pre-wrap leading-relaxed">{entry.body}</p>
          ) : null}
        </Card>
      ))}
    </div>
  );
};

const AccessSpotlight = ({ bot, snapshot }: { bot: Bot; snapshot?: AccessSnapshot | null }) => {
  const github = snapshot?.github_access || extractGithubAccess(bot.metadata);
  const database = snapshot?.database_access || extractDatabaseAccess(bot.metadata);
  const repos = github?.repos || [];
  const databases = database?.databases || [];
  const [diagramModalOpen, setDiagramModalOpen] = useState(false);

  if (!github && !database) return null;

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 rounded-2xl border border-slate-700 bg-slate-900/80 px-4 py-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.25em] text-slate-400">Vue des accès</p>
          <h3 className="text-sm font-bold text-white">Lecture rapide des permissions du bot</h3>
        </div>
        <button
          type="button"
          onClick={() => setDiagramModalOpen(true)}
          className="group inline-flex items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-indigo-600 via-violet-600 to-indigo-600 bg-[length:200%_100%] px-5 py-2.5 text-sm font-bold text-white shadow-lg shadow-indigo-900/40 transition hover:bg-right hover:shadow-indigo-700/50"
        >
          <Workflow className="h-4 w-4 transition group-hover:scale-110" />
          Diagramme visuel — plein écran
        </button>
      </div>

      <BotAccessDiagramModal
        isOpen={diagramModalOpen}
        onClose={() => setDiagramModalOpen(false)}
        botName={bot.name}
        botIdentifier={bot.id || bot.botSlug || bot.name}
        botAvatar={bot.avatar}
        githubSummary={github?.summary}
        databaseSummary={database?.summary}
        matchedRole={database?.matched_role}
        repos={repos}
        databases={databases}
        accessUsers={snapshot?.access_users || []}
        owner={bot.owner}
        pilots={bot.pilots}
      />

      <div className="grid gap-4 lg:grid-cols-2">
        <div className="rounded-2xl border border-slate-700 bg-slate-900 p-5">
          <div className="flex items-center justify-between gap-3">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.25em] text-indigo-300">GitHub</p>
              <h3 className="mt-1 text-xl font-black text-white">Accès dépôts</h3>
            </div>
            <div className="rounded-full border border-indigo-500/30 bg-indigo-500/15 px-3 py-1 text-xs font-bold text-indigo-100">
              {repos.length} repo{repos.length > 1 ? 's' : ''}
            </div>
          </div>
          <p className="mt-3 text-sm text-slate-300">{github?.summary || 'Aucune information GitHub synchronisée.'}</p>
          <div className="mt-4 space-y-3">
            {repos.length ? repos.map((repo, index) => (
              <div key={`${repo.repo}-${index}`} className="rounded-xl border border-slate-700 bg-slate-950 p-3">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="rounded-full bg-indigo-500/20 px-2.5 py-1 text-xs font-semibold text-indigo-100">{repo.remote_name || 'origin'}</span>
                  <span className="text-sm font-bold text-white">{repo.repo || repo.url || 'Repo GitHub'}</span>
                  {repo.verified ? <span className="rounded-full bg-emerald-500/20 px-2 py-0.5 text-[11px] font-bold text-emerald-100">vérifié</span> : null}
                  {repo.private ? <span className="rounded-full bg-slate-700/80 px-2 py-0.5 text-[11px] font-bold text-slate-100">privé</span> : null}
                  {repo.access_level ? <span className="rounded-full bg-indigo-950/70 px-2 py-0.5 text-[11px] font-bold text-indigo-100">{repo.access_level}</span> : null}
                </div>
                {repo.repo_path ? <p className="mt-2 text-xs font-mono text-slate-300 break-all">{repo.repo_path}</p> : null}
                <div className="mt-2 flex flex-wrap gap-2 text-[11px] text-slate-300">
                  {repo.source_kind ? <span>source: {repo.source_kind}</span> : null}
                  {repo.remote_name ? <span>remote: {repo.remote_name}</span> : null}
                </div>
                {repo.url ? <p className="mt-1 text-xs text-slate-400 break-all">{repo.url}</p> : null}
              </div>
            )) : (
              <div className="rounded-xl border border-dashed border-white/10 p-4 text-sm text-slate-400">Aucun remote GitHub détecté localement.</div>
            )}
          </div>
        </div>

        <div className="rounded-2xl border border-slate-700 bg-slate-900 p-5">
          <div className="flex items-center justify-between gap-3">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.25em] text-emerald-300">Bases de données</p>
              <h3 className="mt-1 text-xl font-black text-white">Accès détectés</h3>
            </div>
            <div className="rounded-full border border-emerald-500/30 bg-emerald-500/15 px-3 py-1 text-xs font-bold text-emerald-100">
              {databases.length} BD
            </div>
          </div>
          <p className="mt-3 text-sm text-slate-300">{database?.summary || 'Aucune information base de données synchronisée.'}</p>
          <div className="mt-3 flex flex-wrap gap-2 text-xs">
            {database?.matched_role ? <span className="rounded-full bg-emerald-500/20 px-2.5 py-1 font-semibold text-emerald-100">Rôle: {database.matched_role}</span> : null}
            {database?.source ? <span className="rounded-full bg-white/10 px-2.5 py-1 font-semibold text-slate-200">Source: {database.source}</span> : null}
          </div>
          <div className="mt-4 space-y-3">
            {databases.length ? databases.map((db, index) => (
              <div key={`${db.database}-${index}`} className="rounded-xl border border-slate-700 bg-slate-950 p-3">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-sm font-bold text-white">{db.database || 'Database'}</span>
                  {db.is_prisme_agent_info ? <span className="rounded-full bg-fuchsia-500/20 px-2 py-0.5 text-[11px] font-bold text-fuchsia-100">PRIsME_agent_info</span> : null}
                  {db.is_owner ? <span className="rounded-full bg-emerald-500/20 px-2 py-0.5 text-[11px] font-bold text-emerald-100">owner</span> : null}
                  {db.can_connect ? <span className="rounded-full bg-sky-500/20 px-2 py-0.5 text-[11px] font-bold text-sky-100">connect</span> : null}
                </div>
                {db.owner ? <p className="mt-2 text-xs text-slate-300">Owner: <span className="font-mono">{db.owner}</span></p> : null}
              </div>
            )) : (
              <div className="rounded-xl border border-dashed border-slate-700 bg-slate-950 p-4 text-sm text-slate-300">
                Aucune BD confirmée automatiquement pour ce bot avec la règle hostname → rôle PostgreSQL.
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const formatMoney = (value: number | null | undefined, currency: string) => {
  if (value == null) return '—';
  try {
    return new Intl.NumberFormat('fr-CA', { style: 'currency', currency, maximumFractionDigits: 0 }).format(value);
  } catch {
    return `${Math.round(value).toLocaleString('fr-CA')} ${currency}`;
  }
};

const formatNumber = (value: number | null | undefined) =>
  value == null ? '—' : value.toLocaleString('fr-CA');

const RoiKpi = ({
  icon: Icon,
  label,
  value,
  sub,
  tone = 'slate',
}: {
  icon: React.ElementType;
  label: string;
  value: string;
  sub?: string;
  tone?: 'emerald' | 'rose' | 'indigo' | 'amber' | 'slate';
}) => {
  const tones: Record<string, string> = {
    emerald: 'border-emerald-400/30 bg-emerald-500/10 text-emerald-200',
    rose: 'border-rose-400/30 bg-rose-500/10 text-rose-200',
    indigo: 'border-indigo-400/30 bg-indigo-500/10 text-indigo-200',
    amber: 'border-amber-400/30 bg-amber-500/10 text-amber-200',
    slate: 'border-white/10 bg-slate-800/50 text-slate-200',
  };
  return (
    <div className={`rounded-2xl border p-4 ${tones[tone]}`}>
      <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide opacity-80">
        <Icon className="w-4 h-4" />
        {label}
      </div>
      <div className="mt-2 text-2xl font-black text-white">{value}</div>
      {sub ? <div className="mt-1 text-xs text-slate-400">{sub}</div> : null}
    </div>
  );
};

const RoiTab = ({ roi, loading, error }: { roi: RoiData | null; loading: boolean; error: string | null }) => {
  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="rounded-lg border border-indigo-400/20 bg-indigo-500/10 px-4 py-3 text-sm font-semibold text-indigo-100">
          Calcul du ROI à partir de la base PRIsME_agent_info…
        </div>
      </div>
    );
  }

  if (error || !roi) {
    return <DataPlaceholder title={error || 'Retour sur Investissement & Coûts'} />;
  }

  const { currency } = roi;
  const positive = (roi.roi.net ?? 0) >= 0;
  const maxTimeline = Math.max(1, ...roi.timeline.map((t) => Math.max(t.requests, t.activities)));

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h3 className="text-lg font-bold text-white">ROI & Coûts — {roi.period.label}</h3>
          <p className="text-xs text-slate-400">
            {roi.period.since ? `Du ${roi.period.since}` : 'Historique'}{roi.period.until ? ` au ${roi.period.until}` : ''} · {roi.period.monthsCovered} mois
          </p>
        </div>
        <span className="rounded-full border border-white/10 bg-slate-800/60 px-3 py-1 text-xs font-semibold text-slate-300">
          Devise : {currency}
        </span>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <RoiKpi icon={TrendingUp} label="Valeur générée" value={formatMoney(roi.value.amount, currency)} sub={roi.value.basis} tone="emerald" />
        <RoiKpi icon={Wallet} label="Coûts totaux" value={formatMoney(roi.costs.total, currency)} sub={`Fixes + usage`} tone="rose" />
        <RoiKpi
          icon={positive ? TrendingUp : TrendingDown}
          label="ROI net"
          value={formatMoney(roi.roi.net, currency)}
          sub={roi.roi.percent != null ? `${roi.roi.percent > 0 ? '+' : ''}${roi.roi.percent}%` : 'ROI % indisponible'}
          tone={positive ? 'emerald' : 'rose'}
        />
        <RoiKpi
          icon={Gauge}
          label="Ratio valeur / coût"
          value={roi.roi.ratio != null ? `${roi.roi.ratio}×` : '—'}
          sub={roi.roi.ratio != null ? 'Pour 1 $ investi' : 'Coûts non définis'}
          tone="indigo"
        />
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
        <RoiKpi icon={ListChecks} label="Requêtes traitées" value={formatNumber(roi.totals.requests)} tone="slate" />
        <RoiKpi icon={Activity} label="Activités loggées" value={formatNumber(roi.totals.activities)} tone="slate" />
        <RoiKpi icon={Clock} label="Temps économisé" value={`${formatNumber(roi.totals.minutesSaved)} min`} sub={roi.totals.minutesSaved > 0 ? `≈ ${Math.round(roi.totals.minutesSaved / 60)} h` : undefined} tone="amber" />
      </div>

      {roi.totals.humanMinutesWithout > 0 || roi.totals.humanMinutesWith > 0 ? (
        <Card>
          <CardTitle>Temps humain : sans bot vs avec bot</CardTitle>
          {(() => {
            const without = roi.totals.humanMinutesWithout;
            const withBot = roi.totals.humanMinutesWith;
            const max = Math.max(without, withBot, 1);
            const Row = ({ label, minutes, color }: { label: string; minutes: number; color: string }) => (
              <div className="flex items-center gap-3">
                <span className="w-28 shrink-0 text-xs text-slate-400">{label}</span>
                <div className="flex-1 h-4 rounded-full bg-slate-900/60 overflow-hidden">
                  <div className={`h-full ${color}`} style={{ width: `${Math.max(3, (minutes / max) * 100)}%` }} />
                </div>
                <span className="w-32 shrink-0 text-right text-xs text-slate-300">
                  {formatNumber(minutes)} min <span className="text-slate-500">(≈ {Math.round(minutes / 60)} h)</span>
                </span>
              </div>
            );
            return (
              <div className="space-y-2">
                <Row label="Sans le bot" minutes={without} color="bg-rose-500/80" />
                <Row label="Avec le bot" minutes={withBot} color="bg-indigo-500/80" />
                <Row label="Économisé" minutes={roi.totals.minutesSaved} color="bg-emerald-500/80" />
              </div>
            );
          })()}
        </Card>
      ) : null}


      {roi.timeline.length > 0 ? (
        <Card>
          <CardTitle>Activité mensuelle</CardTitle>
          <div className="space-y-2 mt-2">
            {roi.timeline.map((t) => (
              <div key={t.month} className="flex items-center gap-3">
                <span className="w-16 shrink-0 text-xs font-mono text-slate-400">{t.month}</span>
                <div className="flex-1 space-y-1">
                  <div className="h-2.5 rounded-full bg-indigo-500/80" style={{ width: `${Math.max(4, (t.requests / maxTimeline) * 100)}%` }} title={`${t.requests} requêtes`} />
                  <div className="h-2.5 rounded-full bg-emerald-500/70" style={{ width: `${Math.max(4, (t.activities / maxTimeline) * 100)}%` }} title={`${t.activities} activités`} />
                </div>
                <span className="w-24 shrink-0 text-right text-xs text-slate-400">{t.requests} req · {t.activities} act</span>
              </div>
            ))}
          </div>
          <div className="mt-3 flex gap-4 text-xs text-slate-400">
            <span className="flex items-center gap-1"><span className="inline-block w-3 h-2 rounded-full bg-indigo-500/80" /> Requêtes</span>
            <span className="flex items-center gap-1"><span className="inline-block w-3 h-2 rounded-full bg-emerald-500/70" /> Activités</span>
          </div>
        </Card>
      ) : null}

      <div className="grid lg:grid-cols-2 gap-4">
        <Card>
          <CardTitle>Coûts fixes</CardTitle>
          {roi.costs.fixedItems.length > 0 ? (
            <div className="space-y-2">
              {roi.costs.fixedItems.map((item, i) => (
                <div key={`${item.label}-${i}`} className="flex items-center justify-between rounded-lg bg-slate-900/50 px-3 py-2">
                  <div>
                    <p className="text-sm font-semibold text-white">{item.label}</p>
                    <p className="text-xs text-slate-400">{item.cadence}</p>
                  </div>
                  <span className="text-sm font-bold text-rose-200">{formatMoney(item.amount, currency)}</span>
                </div>
              ))}
              <div className="flex items-center justify-between border-t border-white/10 pt-2 text-sm">
                <span className="text-slate-300">Équivalent mensuel</span>
                <span className="font-bold text-white">{formatMoney(roi.costs.fixedMonthly, currency)}</span>
              </div>
            </div>
          ) : (
            <p className="text-sm text-slate-400">Aucun coût fixe défini dans <code className="rounded bg-black/30 px-1">bot_roi_fixed_costs</code>.</p>
          )}
        </Card>

        <Card>
          <CardTitle>Requêtes par statut</CardTitle>
          {roi.totals.requestsByStatus.length > 0 ? (
            <div className="space-y-2">
              {roi.totals.requestsByStatus.map((s) => (
                <div key={s.status} className="flex items-center justify-between rounded-lg bg-slate-900/50 px-3 py-2">
                  <span className="text-sm text-slate-200 capitalize">{s.status || 'inconnu'}</span>
                  <span className="text-sm font-bold text-indigo-200">{formatNumber(s.count)}</span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-slate-400">Pas de ventilation de statut disponible.</p>
          )}
        </Card>
      </div>

    </div>
  );
};

// --- MODAL PRINCIPALE ---
export default function BotDetailModal({ bot, bots = [], isOpen, onClose, onSelectBot }: BotDetailModalProps) {
  const [activeTab, setActiveTab] = useState<TabId>('esprit');
  const [agentEntries, setAgentEntries] = useState<OpenClawAgentEntry[]>([]);
  const [entriesLoading, setEntriesLoading] = useState(false);
  const [dbBots, setDbBots] = useState<Bot[]>([]);
  const [roi, setRoi] = useState<RoiData | null>(null);
  const [roiLoading, setRoiLoading] = useState(false);
  const [roiError, setRoiError] = useState<string | null>(null);
  const [accessSnapshot, setAccessSnapshot] = useState<AccessSnapshot | null>(null);

  useEffect(() => {
    if (!isOpen) return;

    const controller = new AbortController();
    fetch('/api/prisme/openclaw-agents/bots', {
      credentials: 'include',
      signal: controller.signal,
    })
      .then((response) => (response.ok ? response.json() : []))
      .then((items: DbBot[]) => {
        if (!Array.isArray(items)) {
          setDbBots([]);
          return;
        }

        setDbBots(items.map((item) => {
          const matchingStaticBot = bots.find((candidate) => candidate.name.toLowerCase() === item.name.toLowerCase());
          return {
            ...matchingStaticBot,
            id: item.id,
            botSlug: item.botSlug,
            name: item.name,
            metadata: item.metadata,
            owner: matchingStaticBot?.owner || String(item.metadata?.owner || 'À définir'),
            spirit: matchingStaticBot?.spirit || item.description || 'Bot OpenClaw synchronisé depuis PRIsME_agent_info.',
            mandate: matchingStaticBot?.mandate || item.description || 'Information synchronisée depuis la base PRIsME_agent_info.',
            pilots: matchingStaticBot?.pilots || String(item.metadata?.pilot || item.primaryChannel || 'À définir'),
            allowed: matchingStaticBot?.allowed || [],
            forbidden: matchingStaticBot?.forbidden || [],
          };
        }));
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') setDbBots([]);
      });

    return () => controller.abort();
  }, [bots, isOpen]);

  useEffect(() => {
    if (!isOpen || !bot?.name) {
      setAgentEntries([]);
      return;
    }

    const controller = new AbortController();
    setEntriesLoading(true);
    const botIdentifier = bot.id || bot.botSlug || bot.name;

    fetch(`/api/prisme/openclaw-agents/${encodeURIComponent(botIdentifier)}/entries`, {
      credentials: 'include',
      signal: controller.signal,
    })
      .then((response) => (response.ok ? response.json() : []))
      .then((entries) => {
        if (Array.isArray(entries)) {
          setAgentEntries(entries);
        } else {
          setAgentEntries([]);
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') {
          setAgentEntries([]);
        }
      })
      .finally(() => {
        if (!controller.signal.aborted) setEntriesLoading(false);
      });

    return () => controller.abort();
  }, [bot?.botSlug, bot?.id, bot?.name, isOpen]);

  useEffect(() => {
    if (!isOpen || !bot?.name) {
      setAccessSnapshot(null);
      return;
    }

    const controller = new AbortController();
    const botIdentifier = bot.id || bot.botSlug || bot.name;

    fetch(`/api/prisme/openclaw-agents/${encodeURIComponent(botIdentifier)}/access-snapshot`, {
      credentials: 'include',
      signal: controller.signal,
    })
      .then((response) => (response.ok ? response.json() : null))
      .then((payload) => {
        if (payload && typeof payload === 'object') setAccessSnapshot(payload as AccessSnapshot);
        else setAccessSnapshot(null);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') setAccessSnapshot(null);
      });

    return () => controller.abort();
  }, [bot?.botSlug, bot?.id, bot?.name, isOpen]);

  useEffect(() => {
    if (!isOpen || activeTab !== 'roi' || !bot?.name) return;

    const controller = new AbortController();
    setRoiLoading(true);
    setRoiError(null);
    const botIdentifier = bot.id || bot.botSlug || bot.name;

    fetch(`/api/prisme/openclaw-agents/${encodeURIComponent(botIdentifier)}/roi`, {
      credentials: 'include',
      signal: controller.signal,
    })
      .then(async (response) => {
        if (response.ok) return response.json();
        if (response.status === 404) throw new Error('Aucune donnée ROI : ce bot n’est pas (encore) dans PRIsME_agent_info.');
        throw new Error('ROI indisponible pour le moment.');
      })
      .then((data: RoiData) => setRoi(data))
      .catch((err) => {
        if (err?.name !== 'AbortError') {
          setRoi(null);
          setRoiError(err.message || 'ROI indisponible.');
        }
      })
      .finally(() => {
        if (!controller.signal.aborted) setRoiLoading(false);
      });

    return () => controller.abort();
  }, [activeTab, bot?.id, bot?.botSlug, bot?.name, isOpen]);

  useEffect(() => {
    setRoi(null);
    setRoiError(null);
  }, [bot?.id, bot?.name]);

  const entriesByTab = useMemo(() => {
    return agentEntries.reduce<Record<string, OpenClawAgentEntry[]>>((acc, entry) => {
      const key = entryTabId(entry);
      if (!key) return acc;
      acc[key] = [...(acc[key] || []), entry];
      return acc;
    }, {});
  }, [agentEntries]);

  const tabs = useMemo(() => {
    if (!bot) return baseTabs;
    return bot.name === 'Flavius'
      ? baseTabs
      : baseTabs.filter((tab) => tab.id !== 'integrations');
  }, [bot]);

  const selectableBots = dbBots.length > 0 ? dbBots : bots;
  const selectedBotKey = bot
    ? bot.id || selectableBots.find((candidate) => candidate.name.toLowerCase() === bot.name.toLowerCase())?.id || bot.name
    : '';

  useEffect(() => {
    if (!bot || bot.id || !onSelectBot || dbBots.length === 0) return;
    const dbBot = dbBots.find((candidate) => candidate.name.toLowerCase() === bot.name.toLowerCase());
    if (dbBot) onSelectBot(dbBot);
  }, [bot, dbBots, onSelectBot]);

  const tabContent = useMemo(() => {
    if (!bot) return null;
    const data = getBotData(bot.name);
    const dynamicEntries = entriesByTab[activeTab] || [];
    const dynamicContent = dynamicEntries.length > 0
      ? <DynamicEntries entries={dynamicEntries} emptyTitle="Information" />
      : null;

    switch (activeTab) {
      case 'esprit':
        return (
          <div className="space-y-4">
            <Card><CardTitle>Esprit</CardTitle><p className="text-slate-300 leading-7">{bot.spirit}</p></Card>
            <Card><CardTitle>Mandat</CardTitle><p className="text-slate-300 leading-7">{bot.mandate}</p></Card>
          </div>
        );

      case 'securite':
        return (
          <div className="space-y-4">
            {data.security?.location && (
              <Card>
                <CardTitle>Localisation & Réseau</CardTitle>
                <p className="text-sm text-slate-300 flex items-center gap-2"><MapPin size={14} /> {data.security.location}</p>
                <p className="text-sm text-slate-300 flex items-center gap-2 mt-2"><Wifi size={14} /> {data.security.network}</p>
              </Card>
            )}
            {data.security?.risks?.length > 0 && (
              <Card>
                <CardTitle>Risques Identifiés</CardTitle>
                <div className="space-y-3">
                  {data.security.risks.map((risk: any) => (
                    <div key={risk.title} className="p-3 rounded-lg bg-slate-900/50">
                      <div className="flex justify-between items-center">
                        <h5 className="font-semibold text-white">{risk.title}</h5>
                        <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${risk.level === 'Élevé' ? 'bg-rose-500/20 text-rose-200' : 'bg-amber-500/20 text-amber-200'}`}>
                          {risk.level}
                        </span>
                      </div>
                      <p className="text-xs text-slate-400 mt-1">{risk.description}</p>
                    </div>
                  ))}
                </div>
              </Card>
            )}
            <DataPlaceholder title="Logs de Sécurité & Audit" />
          </div>
        );

      case 'acces':
        return (
          <div className="space-y-4">
            <AccessSpotlight bot={bot} snapshot={accessSnapshot} />
            {data.access ? (
              <div className="space-y-3">
                {Object.entries(data.access).map(([key, value]) => (
                  <Card key={key}>
                    <CardTitle>{key}</CardTitle>
                    <p className="text-sm text-slate-300 whitespace-pre-wrap leading-relaxed">{value as string}</p>
                  </Card>
                ))}
              </div>
            ) : (!extractGithubAccess(bot.metadata) && !extractDatabaseAccess(bot.metadata)) ? <DataPlaceholder title="Accès Détaillés" /> : null}
          </div>
        );

      case 'skills':
        if (dynamicContent) return dynamicContent;
        return data.skills ? (
          <div className="space-y-4">
            {Object.entries(data.skills).map(([category, list]) => (
              <Card key={category}>
                <CardTitle>{category}</CardTitle>
                <div className="flex flex-wrap gap-2 mt-2">
                  {(list as string[]).map(skill => (
                    <span key={skill} className="text-xs bg-slate-700 text-slate-200 px-2.5 py-1 rounded-full font-mono">
                      {skill}
                    </span>
                  ))}
                </div>
              </Card>
            ))}
          </div>
        ) : <DataPlaceholder title="Skills Détaillés" />;

      case 'ai':
        if (dynamicContent) return dynamicContent;
        return data.ai ? (
          <div className="space-y-4">
            <Card><CardTitle>Provider</CardTitle><p className="text-slate-300">{data.ai.provider}</p></Card>
            <Card>
              <CardTitle>Cascade de Modèles</CardTitle>
              <div className="space-y-3">
                {data.ai.cascade.map((c: any) => (
                  <div key={c.level} className="p-3 rounded-lg bg-slate-900/50">
                    <h5 className="font-semibold text-white">{c.level}: <span className="text-indigo-300">{c.model}</span></h5>
                    <p className="text-xs text-slate-400 mt-1">{c.usage}</p>
                  </div>
                ))}
              </div>
            </Card>
            <Card><CardTitle>Logique de Bascule</CardTitle><p className="text-sm text-slate-300">{data.ai.logic}</p></Card>
          </div>
        ) : <DataPlaceholder title="Configuration IA" />;

      case 'cron':
        if (dynamicContent) return dynamicContent;
        return data.cron ? (
          <div className="space-y-4">
            <Card>
              <CardTitle>Tâches Récurrentes (HEARTBEAT.md)</CardTitle>
              <ul className="list-disc list-inside text-sm text-slate-300 space-y-2">
                {data.cron.jobs.map((job: string) => <li key={job}>{job}</li>)}
              </ul>
            </Card>
            <DataPlaceholder title="Historique des Exécutions" />
          </div>
        ) : <DataPlaceholder title="Jobs Récurrents" />;

      case 'telegram':
        if (dynamicContent) return dynamicContent;
        return data.telegram ? (
          <div className="space-y-4">
            <Card><CardTitle>Politique d'Accès</CardTitle><p className="text-slate-300">{data.telegram.policy}</p></Card>
            <Card><CardTitle>Streaming des Réponses</CardTitle><p className="text-slate-300">{data.telegram.streaming}</p></Card>
            <Card className="!border-amber-500/30">
              <CardTitle>Capacités Spéciales</CardTitle>
              <p className="text-amber-200 text-sm">{data.telegram.special_powers}</p>
            </Card>
          </div>
        ) : <DataPlaceholder title="Configuration Telegram" />;

      case 'integrations':
        return bot.name === 'Flavius'
          ? <BotProjetIntegrationPlanTab />
          : <DataPlaceholder title="Plan d’intégrations" />;

      case 'roi':
        return <RoiTab roi={roi} loading={roiLoading} error={roiError} />;

      default:
        return null;
    }
  }, [accessSnapshot, activeTab, bot, entriesByTab, roi, roiLoading, roiError]);

  if (!isOpen || !bot) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="absolute inset-0 bg-black/80 backdrop-blur-md"
          onClick={onClose}
        />

        <motion.div
          initial={{ opacity: 0, y: 20, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 20, scale: 0.95 }}
          transition={{ duration: 0.2, ease: "circOut" }}
          className={`relative z-10 w-full max-w-5xl h-full max-h-[90vh] rounded-2xl flex flex-col overflow-hidden shadow-2xl ${
            bot.deceased
              ? 'border-2 border-rose-600/80 bg-slate-900 shadow-[0_0_60px_rgba(220,38,38,0.35)]'
              : 'border border-slate-700 bg-slate-900'
          }`}
        >
          {bot.deceased ? (
            <div className="relative shrink-0 overflow-hidden border-b border-rose-600/50 bg-gradient-to-r from-rose-950 via-red-950 to-rose-950 px-6 py-5">
              <div className="absolute inset-0 flex items-center justify-center overflow-hidden opacity-30">
                <span className="-rotate-6 text-6xl font-black uppercase tracking-[0.3em] text-rose-600">DÉCÉDÉ</span>
              </div>
              <motion.div
                className="relative flex flex-col items-center gap-3 sm:flex-row sm:justify-center"
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ type: 'spring', stiffness: 260, damping: 18 }}
              >
                <Skull className="hidden h-10 w-10 text-rose-400 sm:block" strokeWidth={2} />
                <div className="relative">
                  <motion.div
                    className="absolute inset-0 rounded-full bg-rose-600/40 blur-xl"
                    animate={{ scale: [1, 1.2, 1], opacity: [0.4, 0.8, 0.4] }}
                    transition={{ duration: 2, repeat: Infinity }}
                  />
                  <X className="relative h-20 w-20 text-rose-500 drop-shadow-[0_0_20px_rgba(239,68,68,0.8)]" strokeWidth={4} />
                </div>
                <div className="text-center sm:text-left">
                  <p className="text-xs font-bold uppercase tracking-[0.45em] text-rose-300">Agent retiré du service</p>
                  <h1 className="text-3xl font-black uppercase tracking-wide text-white line-through decoration-rose-500 decoration-4">
                    {bot.name}
                  </h1>
                </div>
                <Skull className="hidden h-10 w-10 text-rose-400 sm:block" strokeWidth={2} />
              </motion.div>
              <button onClick={onClose} className="absolute right-4 top-4 z-10 rounded-full bg-black/40 p-2 hover:bg-black/60 transition-colors">
                <X className="w-5 h-5 text-white" />
              </button>
            </div>
          ) : (
          <header className="p-6 border-b border-slate-800">
            <button onClick={onClose} className="absolute right-4 top-4 rounded-full bg-white/10 p-2 hover:bg-white/20 transition-colors">
              <X className="w-5 h-5" />
            </button>
            <div className="flex items-center gap-4">
              {bot.avatar ? (
                <img
                  src={bot.avatar}
                  alt={bot.name}
                  className="w-14 h-14 rounded-full border-2 border-amber-400/40 object-cover shadow-lg"
                />
              ) : null}
              <div>
                <h1 className="text-2xl font-bold text-white">{bot.name}</h1>
                <p className="text-sm text-slate-400">Owner: {bot.owner} | Pilotes: {bot.pilots}</p>
              </div>
              {selectableBots.length > 0 && onSelectBot ? (
                <div className="ml-auto mr-12 min-w-[220px]">
                  <label className="sr-only" htmlFor="bot-select">Selectionner un bot</label>
                  <select
                    id="bot-select"
                    value={selectedBotKey}
                    onChange={(event) => {
                      const selected = selectableBots.find((candidate) => (candidate.id || candidate.name) === event.target.value);
                      if (selected) onSelectBot(selected);
                    }}
                    className="w-full rounded-lg border border-white/10 bg-slate-950 px-3 py-2 text-sm font-semibold text-white outline-none transition-colors hover:border-indigo-400/50 focus:border-indigo-400"
                  >
                    {selectableBots.map((candidate) => (
                      <option key={candidate.id || candidate.name} value={candidate.id || candidate.name}>
                        {candidate.name}
                      </option>
                    ))}
                  </select>
                </div>
              ) : null}
            </div>
          </header>
          )}

          <div className={`grid grid-cols-[260px_minmax(0,1fr)] flex-1 min-h-0 ${bot.deceased ? 'opacity-40 grayscale' : ''}`}>
            <aside className="border-r border-slate-800 p-4 overflow-y-auto">
              <nav className="space-y-1">
                {tabs.map((tab) => {
                  const Icon = tab.icon;
                  const active = activeTab === tab.id;
                  return (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      className={`w-full text-sm rounded-lg px-3 py-2.5 text-left transition-all flex items-center gap-3 ${active ? 'bg-indigo-600 text-white font-semibold' : 'text-slate-300 hover:bg-slate-800'}`}
                    >
                      <Icon className="w-5 h-5" />
                      <span>{tab.label}</span>
                    </button>
                  );
                })}
              </nav>
            </aside>

            <main className="relative min-h-0 overflow-y-auto p-6 bg-slate-950/50">
              {entriesLoading && ['skills', 'ai', 'cron', 'telegram'].includes(activeTab) ? (
                <div className="mb-4 rounded-lg border border-indigo-400/20 bg-indigo-500/10 px-3 py-2 text-xs font-semibold text-indigo-100">
                  Chargement des informations du bot...
                </div>
              ) : null}
              {tabContent}
              {bot.deceased ? (
                <div className="pointer-events-none absolute inset-0 flex items-center justify-center bg-black/30">
                  <p className="rounded-2xl border border-rose-500/50 bg-rose-950/90 px-8 py-4 text-center text-lg font-bold uppercase tracking-widest text-rose-200 shadow-xl">
                    Archives — agent hors service
                  </p>
                </div>
              ) : null}
            </main>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
}
